import { _ } from "../../polyglot/polyglot.js";
import { getResult } from "../../helpers/functional-util.js";
import { guid } from "../../helpers/string-util.js";
import { timesSolidIcon } from "../../elements/icons/index.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import ItemPicker from "../ItemPicker/ItemPicker.js";

/**
 * @typedef Item
 * @type {object}
 * @property {Renderable} label - How to render the item
 * @property {function(string): bool} [match] - Must return true if
 *   the item should be displayed given the search string passed as
 *   argument
 * @property {boolean} [selected=false] - Should be true if the item
 *   is selected by default
 */

/**
 * This widget is used to render a basic dropdown with a title and a
 * list of selectable items. When selecting an item in the dropdown
 * list, onSelection() is called with the selected item passed as
 * argument.
 *
 * @param {Renderable} title - Content of the button opening
 *   the list. If it is a function, it must return a renderable
 * @param {Item[]|Promise<Item[]>} items - Content of the dropdown
 *   list
 * @param {bool} [liveSearch] - If true, the default, a search field
 *   is displayed if the length of `items` is greater than
 *   `liveSearchThreshold`. If falsy, no search field is displayed. For
 *   the live search to work, each item in `items` must have a `match()`
 *   method
 * @param {integer} [liveSearchThreshold] - If the length of `items`
 *   is greater than this value and if `liveSearch` is true, a
 *   search field is rendered
 * @param {function} onSelection - Called when an item is selected. It
 *   takes the selected item as argument
 * @param {string} [dataTestName] - A string to be used to find the
 *   dropdown button within the test framework
 * @param {string} [cssClass] - Additional classes added to the container
 * @param {function} [onMouseDown] - Called just before the button
 *   is clicked
 * @param {function} [onContentChange] - Called when the content of the dropdown
 *   changes. This method is invoked with the content items as argument
 */
export default class BaseDropdownWidget extends Widget {
	constructor({
		title,
		cssClass = "",
		dataTestName = "",
		onMouseDown = () => {},
		onSelection = () => {},
		onContentChange = () => {},
		items,
		itemRenderer,
		liveSearch,
		liveSearchThreshold,
		standalone,
	}) {
		super(...arguments);

		this._title = title;
		this._dataTestName = dataTestName;
		this._cssClass = cssClass;
		this._onMouseDown = onMouseDown;
		this._onSelection = onSelection;
		this._onContentChange = onContentChange;
		this._isLoading = true;
		this._isError = false;

		this._itemPicker = this._makeItemPicker({
			items,
			itemRenderer,
			liveSearch,
			liveSearchThreshold,
			standalone,
		});
	}

	/**
	 * @deprecated Prefer using onContentChange.
	 */
	getSelected() {
		return this._itemPicker.getSelected();
	}

	/**
	 * @deprecated Prefer using the items passed to the constructor.
	 */
	getItems() {
		return this._itemPicker.getItems();
	}

	/**
	 * @deprecated Prefer using onContentChange.
	 */
	getValue() {
		return this._itemPicker.getValue();
	}

	/**
	 * @deprecated Prefer using the `selected` field of the items
	 * passed to the constructor.
	 */
	selectValue(value) {
		this._itemPicker.selectValue(value);
	}

	renderContentOn(html) {
		if (this._isError) {
			this.renderErrorContentOn(html);
			return;
		}

		if (this._isLoading) {
			this.renderLoadingContentOn(html);
			return;
		}

		const buttonId = guid();

		html.div(
			{
				class: ["btn-group", "dropdown", this._cssClass],
			},
			this._buttonRenderer({ buttonId }),
			this._listRenderer({ buttonId }),
		);
	}

	renderErrorContentOn(html) {
		html.render(
			timesSolidIcon({
				class: "async-error",
				title: _("Error while fetching items").toString(),
			}),
		);
	}

	renderLoadingContentOn(html) {
		html.div({ class: "css-loader" });
	}

	_makeItemPicker({
		items,
		itemRenderer,
		liveSearch,
		liveSearchThreshold,
		standalone,
	}) {
		const itemPicker = new ItemPicker({
			itemRenderer,
			liveSearch,
			liveSearchThreshold,
			standalone,
		});

		itemPicker.selectionChanged.register((item) => {
			this._onSelection(item);
			this.update();
		});

		Promise.resolve(items)
			.catch((error) => {
				// eslint-disable-next-line no-console -- this is for dev purposes
				console.error(error);
				this._isError = true;
			})
			.then((resolvedItems) => {
				this._isLoading = false;
				this._itemPicker.setItems(resolvedItems);
				this._triggerContentChanged({ items: resolvedItems });
			})
			.finally(() => {
				this.update();
			});

		return itemPicker;
	}

	_triggerContentChanged({ items }) {
		this._onContentChange({ items });
	}

	_buttonRenderer({ buttonId }) {
		return (html) => {
			html.button(
				{
					class: "btn btn-default dropdown-toggle",
					id: buttonId,
					"data-toggle": "dropdown",
					"aria-haspopup": true,
					"aria-expanded": true,
					"data-test": this._dataTestName,
					mousedown: () => this._onMouseDown(),
				},
				getResult(this._title),
				html.span({ class: "bs-caret" }, html.span({ class: "caret" })),
			);
		};
	}

	_listRenderer({ buttonId }) {
		return (html) => {
			html.div(
				{
					class: "dropdown-menu",
					"data-test": "dropdown-menu",
					"aria-labelledby": buttonId,
					click: (event) => event.stopPropagation(),
				},
				this._itemPicker,
			);
		};
	}
}
